import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { first } from 'rxjs/operators/first';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { combineLatest } from 'rxjs/observable/combineLatest';

import { ApplicationService, ParentInfo } from '../../services/application/application.service';
import { ErrorService } from '../../services/error/error.service';
import { SplashService } from '../../services/splash/splash.service';
import { UserService, UserInfo } from '../../services/user/user.service';

import { PhonePipe } from '../../pipes/phone.pipe';

import { phoneNumberValidator } from '../../validators/phonenumber.validator';

@Component({
    selector: 'app-parentcontact-component',
    styleUrls: ['parentcontact.component.css'],
    templateUrl: 'parentcontact.component.html',
})
export class ParentContactComponent implements OnInit, OnDestroy {
    private _subRelease: Subject<void> = new Subject<void>();

    contactForm: FormGroup;
    LoadedPhone: string;
    LoadedEmail: string;
    PartialViewReady: boolean;
    ParentInfo: ParentInfo;
    User: UserInfo;

    get phone() {
        return this.contactForm.get('ContactPhone');
    }
    get email() {
        return this.contactForm.get('ContactEmail');
    }

    constructor(
        private appService: ApplicationService,
        private errorService: ErrorService,
        private fb: FormBuilder,
        private phonePipe: PhonePipe,
        private splashService: SplashService,
        private userService: UserService
    ) {}

    ngOnInit() {
        const self = this;

        self.LoadedEmail = null;
        self.LoadedPhone = null;
        self.PartialViewReady = false;
        self.ParentInfo = null;
        self.User = null;

        self.userService.UserSubject.pipe(
            takeUntil(self._subRelease),
            first<UserInfo>(user => user != null && !!user.AuthToken)
        ).subscribe(
            user => {
                self.User = user;

                // Set up constant monitoring of the user object, to catch AuthToken updates.
                self.userService.UserSubject.pipe(takeUntil(self._subRelease)).subscribe(_user => {
                    self.User = _user;
                });

                // Load student info so we can get access to the student ID and the current contact info
                combineLatest(self.appService.GetParentContactInformation(self.User.AuthToken), function(parent) {
                    return {
                        Parent: parent,
                    };
                })
                    .pipe(takeUntil(self._subRelease))
                    .subscribe(
                        packet => {
                            self.ParentInfo = packet.Parent;
                            self.LoadedEmail = self.ParentInfo && self.ParentInfo.Email;

                            const formattedPhone = self.phonePipe.transform(self.ParentInfo && self.ParentInfo.Phone, 'parensdash');
                            self.LoadedPhone = formattedPhone;

                            if (!self.contactForm) {
                                self.contactForm = self.fb.group({
                                    ContactPhone: [formattedPhone, phoneNumberValidator()],
                                    ContactEmail: [self.LoadedEmail, Validators.pattern(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i)],
                                });
                            } else {
                                self.contactForm.setValue({
                                    ContactPhone: formattedPhone,
                                    ContactEmail: self.LoadedEmail,
                                });
                            }

                            self.PartialViewReady = true;
                        },
                        error => {
                            self.errorService.HandleServiceError(error);
                        }
                    );
            },
            error => {
                self.errorService.HandleServiceError(error);
            }
        );
    }

    ngOnDestroy() {
        this._subRelease.next();
        this._subRelease.complete();
    }

    // #region Update Parent Contact Info Method

    UpdateParentContactInfo() {
        const self = this;

        const form = self.contactForm.value;

        if (!self.contactForm.valid) {
            return;
        }

        const cleanPhone = form.ContactPhone.replace(/[\(\)\-\s]/g, '');

        this.appService
            .UpdateParentContactInfo(self.User.AuthToken, self.User.UserID, self.User.FocusStaffID, cleanPhone, form.ContactEmail)
            .subscribe(
                info => {
                    const formattedPhone = self.phonePipe.transform(info.Phone, 'parensdash');

                    self.contactForm.setValue({
                        ContactPhone: formattedPhone,
                        ContactEmail: info.Email,
                    });

                    self.LoadedEmail = info.Email;
                    self.LoadedPhone = formattedPhone;

                    self.splashService.SnackbarSuccess('Contact information updated successfully.');
                },
                error => {
                    self.errorService.HandleServiceError(error);
                }
            );
    }

    // #endregion
}



// WEBPACK FOOTER //
// ./src/app/parentcontact/parentcontact.component.ts